import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks, articleMeta, absoluteUrl } from "@/lib/seo";
import { articleHeroFor } from "@/lib/article-heroes";
import { BlogPost, articleJsonLd, breadcrumbJsonLd } from "@/components/BlogPost";

const SLUG = "hei-koulujarjestelma-historia";
const URL = `https://montessori24.fi/montessori-koulu/${SLUG}`;
const TITLE = "HEI-koulujärjestelmän historia — Suomalainen varhaiskasvatus maailmalle";
const DESC = "HEI Schools syntyi Helsingistä ja vei suomalaisen varhaiskasvatuksen maailmalle. Lue, miten HEI-koulujärjestelmä kehittyi ja miten se liittyy kotiin.";
const PUBLISHED = "2026-07-15";
const HERO = articleHeroFor(SLUG);

export const Route = createFileRoute("/montessori-koulu/hei-koulujarjestelma-historia")({
  head: () => ({
    meta: articleMeta({
      slug: SLUG,
      title: TITLE,
      description: DESC,
      published: PUBLISHED,
      image: HERO,
    }),
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks(URL)],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(articleJsonLd({ title: TITLE, description: DESC, url: URL, image: absoluteUrl(HERO), publishedISO: PUBLISHED })) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd(SLUG, TITLE)) },
    ],
  }),
  component: Post,
});

function Post() {
  return (
    <BlogPost
      eyebrow="Historia"
      title={TITLE}
      lead="HEI Schools on yksi tunnetuimmista suomalaisista varhaiskasvatusvientituotteista. Sen tarina alkaa Helsingistä ja jatkuu kansainvälisinä kouluina Aasiassa, Lähi-idässä ja Amerikassa. Tämä artikkeli avaa HEI-koulujärjestelmän historian, pedagogiikan ja sen yhteyden suomalaiseen kotiin."
      readingMinutes={7}
      publishedISO={PUBLISHED}
    >
      <h2>Mikä on HEI Schools?</h2>
      <p><strong>HEI Schools</strong> on Helsingistä lähtöisin oleva varhaiskasvatuskonsepti, joka perustuu Suomen kansalliseen varhaiskasvatukseen ja tutkimukseen. Nimi HEI tulee sanoista <em>Helsinki</em> ja <em>International</em> — se on sekä tervehdys että viittaus juuriinsa.</p>
      <p>HEI Schools ei ole yksittäinen koulu vaan lisensoitu kokonaisuus, joka sisältää pedagogiikan, oppimisympäristön, opettajankoulutuksen, digitaaliset työkalut ja brändätyn konseptin. Se on suunnattu erityisesti kansainvälisille markkinoille, joilla suomalainen kasvatus herättää kiinnostusta.</p>
      <p>Yhtiö perustettiin Helsingissä vuonna 2015. Sen taustalla ovat <strong>Helsingin yliopiston kasvatustieteellinen tiedekunta</strong> sekä joukko suomalaisia opetusalan ja liiketoiminnan ammattilaisia. Ensimmäinen kansainvälinen HEI Schools -yksikkö avattiin <strong>Pekingissä vuonna 2017</strong>, ja sen jälkeen konsepti on levinnyt useisiin maihin.</p>

      <h2>Suomalainen varhaiskasvatus — pohja, jolle HEI rakentui</h2>
      <p>HEI Schools nojaa vahvasti suomalaiseen varhaiskasvatukseen, joka on rakennettu seuraaville pilareille:</p>
      <ul>
        <li><strong>Leikki oppimisen muotona</strong> — lapsi oppii leikin kautta, ei suorana opetuksena.</li>
        <li><strong>Osallisuus ja demokratia</strong> — lapsen ääni kuuluu ja hänen valintansa kunnioitetaan.</li>
        <li><strong>Läheinen suhde luontoon</strong> — ulkoilu, liikkuminen ja aistihavainnot ovat keskiössä.</li>
        <li><strong>Oppimisen ilo</strong> — innostus ja uteliaisuus ovat tärkeämpiä kuin suorittaminen.</li>
        <li><strong>Yksilöllisyys</strong> — jokainen lapsi etenee omassa tahdissaan.</li>
      </ul>
      <p>Nämä periaatteet ovat tuttuja myös montessorilaisesta pedagogiikasta. Ero on siinä, että HEI korostaa enemmän yhteisöllisyyttä, leikkiä ja suomalaista kulttuuria, kun taas Montessori painottaa strukturoitua ympäristöä ja itsenäistä työskentelyä.</p>

      <h2>HEI Schools maailmalla</h2>
      <p>Ensimmäisen Pekingin-koulun jälkeen HEI Schools on laajentunut nopeasti. Kouluja ja oppimiskeskuksia on avattu muun muassa Kiinassa, Vietnamissa, Indonesiassa, Etelä-Koreassa, Arabiemiraateissa, Saudi-Arabiassa ja Yhdysvalloissa.</p>
      <p>Kasvu perustuu kolmeen tekijään:</p>
      <ol>
        <li><strong>Suomalaisen koulutusviennin kysyntä</strong> — maailmalla halutaan oppia Suomen menestystä Pisa-tutkimuksissa.</li>
        <li><strong>Valmis konsepti</strong> — HEI tarjoaa kaiken valmiina, mikä helpottaa kansainvälisten kumppaneiden käynnistystä.</li>
        <li><strong>Tutkimuspohja</strong> — konsepti nojaa suomalaiseen kasvatustieteeseen ja jatkuvaan kehitystyöhön.</li>
      </ol>

      <h2>Miten HEI näkyy suomalaisessa kodissa?</h2>
      <p>Vaikka HEI Schools on kansainvälinen brändi, sen periaatteet sopivat hyvin myös suomalaiseen kotiin. Vanhempi voi tukea lapsen kehitystä HEI-henkisesti esimerkiksi näin:</p>
      <ul>
        <li><strong>Tee arjesta leikkiä</strong> — ruoanlaitto, pyykkien viikkaus ja siivous ovat lapselle yhtä arvokkaita oppimistilanteita kuin lelut.</li>
        <li><strong>Anna aikaa ulkoilulle</strong> — säällä kuin säällä ulkona liikkuminen on suomalaisen kasvatuksen ydin.</li>
        <li><strong>Kunnioita lapsen valintoja</strong> — tarjoa rajattuja vaihtoehtoja ja anna lapsen päättää.</li>
        <li><strong>Luo rauhoittumisen paikka</strong> — lapsen oma nurkkaus, jossa voi levähtää ja säädellä tunteitaan.</li>
      </ul>

      <h2>HEI ja Montessori — kaksi suomalaista suuntausta?</h2>
      <p>HEI Schools ja Montessori eivät ole suomalaisia alkuperältään, mutta molemmat ovat löytäneet tiensä suomalaiseen kasvatuskulttuuriin. HEI ammentaa suomalaisesta varhaiskasvatuksesta, kun taas Montessori on kansainvälinen menetelmä, jota suomalaiset vanhemmat ja päiväkodit ovat soveltaneet paikallisesti.</p>
      <p>Molemmat jakavat saman arvomaailman: lapsi on aktiivinen toimija, joka ansaitsee kunnioitusta ja tilaa kasvaa omassa tahdissaan.</p>

      <h2>Usein kysyttyä</h2>
      <h3>Onko HEI Schools sama kuin suomalainen päiväkoti?</h3>
      <p>Ei täysin. HEI on lisensoitu kansainvälinen konsepti, joka perustuu suomalaiseen varhaiskasvatukseen mutta soveltaa sitä eri kulttuureihin.</p>
      <h3>Voinko soveltaa HEI-periaatteita kotona?</h3>
      <p>Kyllä. HEI-periaatteet — leikki, ulkoilu, osallisuus ja rauhoittuminen — ovat helposti toteutettavissa kotona ilman erityisiä kalusteita.</p>
      <h3>Mikä on HEI:n suhde Montessoriin?</h3>
      <p>Molemmat kunnioittavat lapsen itsenäisyyttä, mutta HEI korostaa enemmän leikkiä ja yhteisöllisyyttä, kun taas Montessori painottaa strukturoitua ympäristöä ja itsenäistä työskentelyä.</p>

      <h2>Yhteenveto</h2>
      <p>HEI Schools on osoitus siitä, että suomalainen varhaiskasvatus kiinnostaa maailmalla. Sen historia on lyhyt mutta vaikuttava: Helsingistä lähtenyt konsepti on levinnyt kymmeniin maihin ja tuonut suomalaisen pedagogiikan tuhansien perheiden arkeen.</p>
      <p>Haluatko rakentaa lapsellesi HEI- ja montessori-henkisen oppimisympäristön kotiin? <Link to="/kauppa">Selaa kalusteita →</Link></p>
    </BlogPost>
  );
}
